import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { EmployerMark } from "@/components/employer/portal/EmployerLayout";
import { API, formatApiError } from "@/components/employer/portal/shared";

const inputClass =
    "w-full border border-ivory/15 bg-ivory/[0.04] px-4 py-3.5 text-sm text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/35 focus:border-champagne";

const lineReveal = {
    hidden: { y: "110%" },
    visible: (i) => ({
        y: 0,
        transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

const post = async (path, body) => {
    const res = await fetch(`${API}/api/employer/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiError(data.detail));
    return data;
};

const Field = ({ label, htmlFor, children }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-ivory/55">
            {label}
        </label>
        {children}
    </div>
);

const AuthShell = ({ mode, children, footer }) => (
    <div className="min-h-screen bg-night text-ivory lg:grid lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden border-r border-ivory/10 lg:flex lg:items-end">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(212,175,55,0.16),transparent_28%),radial-gradient(circle_at_80%_70%,rgba(30,74,52,0.6),transparent_38%)]" />
            <motion.div
                aria-hidden="true"
                className="absolute right-12 top-16 h-72 w-72 border border-champagne/20"
                animate={{ rotate: 360 }}
                transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
            >
                <div className="absolute inset-8 border border-ivory/10" />
                <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-champagne/30" />
            </motion.div>
            <div className="relative p-12 xl:p-16">
                <a href="/" data-testid="employer-auth-home" className="flex items-center gap-4">
                    <EmployerMark />
                    <span className="leading-tight">
                        <span className="block font-serif text-xl font-semibold uppercase tracking-[0.14em]">Afrinexa Global</span>
                        <span className="block text-[9px] font-medium uppercase tracking-[0.32em] text-ivory/50">International Recruitment</span>
                    </span>
                </a>
                <div className="mt-24 max-w-xl">
                    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-champagne">
                        <span className="h-px w-8 bg-champagne" /> Employer Portal
                    </p>
                    <h1 className="mt-7 font-serif text-5xl font-medium leading-[1.02] tracking-tight xl:text-6xl">
                        {["Recruit beyond", "borders with", "total clarity."].map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className="block">
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65, duration: 0.8 }}
                        className="mt-7 max-w-md text-base leading-relaxed text-ivory/65"
                    >
                        Manage company verification, international vacancies, applicants and interviews from one secure workspace.
                    </motion.p>
                </div>
            </div>
        </section>

        <section className="flex min-h-screen items-center px-6 py-14 sm:px-10 lg:px-14">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto w-full max-w-md"
            >
                <div className="mb-10 lg:hidden">
                    <a href="/" data-testid="employer-auth-home-mobile" className="flex items-center gap-4">
                        <EmployerMark />
                        <span className="font-serif text-lg font-semibold uppercase tracking-[0.14em]">Afrinexa Global</span>
                    </a>
                </div>
                <div className="border border-ivory/12 bg-ivory/[0.035] p-7 shadow-2xl shadow-black/20 backdrop-blur sm:p-9">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-champagne">{mode === "login" ? "Welcome back" : "Start hiring"}</p>
                    <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight">
                        {mode === "login" ? "Employer sign in" : "Create employer account"}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-ivory/55">
                        {mode === "login"
                            ? "Access your recruitment dashboard."
                            : "Register your organization. Verification stays pending until Afrinexa reviews it."}
                    </p>
                    <div className="mt-8">{children}</div>
                </div>
                {footer && <div className="mt-7 text-center text-sm text-ivory/55">{footer}</div>}
            </motion.div>
        </section>
    </div>
);

const Submit = ({ busy, label, testid }) => (
    <button
        type="submit"
        data-testid={testid}
        disabled={busy}
        className="group inline-flex w-full items-center justify-center gap-2 bg-ivory px-7 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-champagne disabled:cursor-not-allowed disabled:opacity-75"
    >
        {busy ? <Loader2 size={15} className="animate-spin" /> : null}
        {busy ? "Please wait…" : label}
        {!busy && <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />}
    </button>
);

const ErrorNote = ({ error, testid }) =>
    error ? <p data-testid={testid} className="border-l-2 border-champagne pl-4 text-sm leading-relaxed text-ivory/75">{error}</p> : null;

export default function EmployerLogin() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await post("login", form);
            navigate("/employer");
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <AuthShell
            mode="login"
            footer={<>New employer? <a href="/employer/register" data-testid="employer-login-register-link" className="text-champagne hover:underline">Create an account</a></>}
        >
            <form data-testid="employer-login-form" onSubmit={submit} className="space-y-5">
                <Field label="Business email" htmlFor="el-email">
                    <input id="el-email" data-testid="employer-login-email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="name@company.com" />
                </Field>
                <Field label="Password" htmlFor="el-password">
                    <input id="el-password" data-testid="employer-login-password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} placeholder="••••••••" />
                </Field>
                <ErrorNote error={error} testid="employer-login-error" />
                <Submit busy={busy} label="Sign In" testid="employer-login-button" />
            </form>
        </AuthShell>
    );
}

export function EmployerRegister() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", password: "", phone: "", country: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await post("register", form);
            navigate("/employer/profile");
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    return (
        <AuthShell
            mode="register"
            footer={<>Already registered? <a href="/employer/login" data-testid="employer-register-login-link" className="text-champagne hover:underline">Sign in</a></>}
        >
            <form data-testid="employer-register-form" onSubmit={submit} className="space-y-5">
                <Field label="Company name" htmlFor="er-company">
                    <input id="er-company" data-testid="employer-register-company" type="text" required value={form.company_name} onChange={set("company_name")} className={inputClass} placeholder="Your organization" />
                </Field>
                <Field label="Contact person" htmlFor="er-contact">
                    <input id="er-contact" data-testid="employer-register-contact" type="text" required value={form.contact_name} onChange={set("contact_name")} className={inputClass} placeholder="Full name" />
                </Field>
                <Field label="Business email" htmlFor="er-email">
                    <input id="er-email" data-testid="employer-register-email" type="email" required autoComplete="username" value={form.email} onChange={set("email")} className={inputClass} placeholder="name@company.com" />
                </Field>
                <Field label="Password (min. 8 characters)" htmlFor="er-password">
                    <input id="er-password" data-testid="employer-register-password" type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={set("password")} className={inputClass} placeholder="Choose a strong password" />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Phone / WhatsApp" htmlFor="er-phone">
                        <input id="er-phone" data-testid="employer-register-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+234 ..." />
                    </Field>
                    <Field label="Country" htmlFor="er-country">
                        <input id="er-country" data-testid="employer-register-country" type="text" value={form.country} onChange={set("country")} className={inputClass} placeholder="e.g. Nigeria" />
                    </Field>
                </div>
                <ErrorNote error={error} testid="employer-register-error" />
                <Submit busy={busy} label="Create Employer Account" testid="employer-register-button" />
                <p className="text-xs leading-relaxed text-ivory/40">
                    Employer registration creates an Employer role only. Administrative and recruiter roles are never available publicly.
                </p>
            </form>
        </AuthShell>
    );
}
